import { useEffect, useState } from 'react'
import Button from 'react-bootstrap/Button'
import Modal from 'react-bootstrap/Modal'
import InputGroup from 'react-bootstrap/InputGroup'
import Form from 'react-bootstrap/Form'
import {
   //@ts-ignore
   formatFloatAsCurrencySymbol,
   //@ts-ignore
   formatForSalesforceRichText,
} from '../../../common/helpers.mjs'
import { Description } from '../../ui-helper-elements'
import { Dropdown } from 'react-bootstrap'
import { saveOpportunity } from '../../../ts/data'
// Quote Context
import { useQuoteContext } from '../../../hooks/useQuoteContext'
import useFeaturePermissions from '../../../hooks/useFeaturePermissions'
import {
   currencyObject,
   Discount,
   SFDealDeskType,
   //@ts-ignore
} from '../../../common/types/types.d.ts'
//@ts-ignore
import { DiscountActualEffect } from '../../../common/types/calculate'

import toast from 'react-hot-toast'

enum recordType {
   'New',
   'Expansion',
}

// Interface for additional deal desk fields
interface DealDeskDetails {
   callRecordingLink: string
   relationshipHistory: string
   relationshipContextAns: string
   hasCombinedOffers: string
   nonStandardRequestAns: string
   dealStructureAns: string
   extraDealDeskDetailsAns: string
   additionalInformation: string
   termLength: number
   prepayLength: number
}

interface DealdeskModalButtonProps {
   buttonClass?: string | undefined
   buttonSize?: 'sm' | 'lg' | undefined
   HRImpPreDealdeskPrice: number
   BenAdminImpPreDealdeskPrice: number
   PayrollPreDealdeskPrice: number
   currencyObject: currencyObject
   disableHRISImp: boolean
   disableQuickStart: boolean
   disablePayrollImp: boolean
   HRISImpSelected: boolean
   BenAdminImpSelected: boolean
   QuickStartSelected: boolean
   PayrollImpSelected: boolean
   MRRSelected: boolean
   HRISDisablingDiscountList: DisablingDiscount[]
   QuickstartDisablingDiscountList: DisablingDiscount[]
   payrollDisablingDiscountList: DisablingDiscount[]
   recordType: recordType
   termLength: number
   opportunity_id: string
}

function onPercentInputChange(
   value: number,
   previousValue: number,
   setFunction: (value: number) => void,
   isInvalid: boolean,
   isValid: boolean,
   setValidFunction: (value: boolean) => void,
   setInvalidFunction: (value: boolean) => void,
) {
   if (isInvalid) {
      if (value >= 0) {
         setValidFunction(true)
         setInvalidFunction(false)
      }
   }
   if (isValid) {
      if (value <= 0) {
         setInvalidFunction(true)
         setValidFunction(false)
      }
   }
   setFunction(Math.min(value, 100))
}

function onCommentsChange(
   value: string,
   previousValue: string,
   setFunction: (value: string) => void,
   isInvalid: boolean,
   isValid: boolean,
   setValidFunction: (value: boolean) => void,
   setInvalidFunction: (value: boolean) => void,
) {
   if (isInvalid) {
      if (value.length > 0) {
         setValidFunction(true)
         setInvalidFunction(false)
      }
   }
   if (isValid) {
      if (value.length <= 0) {
         setInvalidFunction(true)
         setValidFunction(false)
      }
   }
   setFunction(value)
}

export default function DealdeskModalButton(props: DealdeskModalButtonProps) {
   const [show, setShow] = useState(false)
   const [formSubmissionAttempted, setFormSubmissionAttempted] = useState(false)
   //TODO: change this to the proper typing once we migrate App.jsx to Typescript
   const state = useQuoteContext()
   const { getString, getFormattedString } = state
   const { hasPermission } = useFeaturePermissions()

   // Determine implementation scenario and calculate pricing
   const isHRISFullOnly =
      (state.impType === 'full' || state.impType === 'premier') &&
      state.benAdminImpType === 'none'
   const isHRISFullWithBenAdmin =
      (state.impType === 'full' || state.impType === 'premier') &&
      state.benAdminImpType === 'full'
   const isQuickstartOnly =
      state.impType === 'quickstart' && state.benAdminImpType === 'none'
   const isQuickstartWithBenAdmin =
      state.impType === 'quickstart' && state.benAdminImpType === 'full'
   const isBenAdminOnly =
      state.impType === 'selfSetup' && state.benAdminImpType === 'full'

   // Dynamic labels for deal desk options
   const hrisImplementationLabel = isHRISFullOnly
      ? 'HRIS Implementation'
      : isHRISFullWithBenAdmin
        ? 'HRIS/Ben Admin Implementation'
        : isBenAdminOnly
          ? 'Ben Admin Imp'
          : 'HRIS/Ben Admin Implementation' // fallback

   const quickstartLabel = isQuickstartOnly
      ? 'QuickStart'
      : isQuickstartWithBenAdmin
        ? 'QuickStart/Ben Admin Imp'
        : 'QuickStart' // fallback

   // Calculate pricing based on scenario
   const hrImplementationListPrice = isHRISFullWithBenAdmin
      ? props.HRImpPreDealdeskPrice + props.BenAdminImpPreDealdeskPrice
      : isBenAdminOnly
        ? props.BenAdminImpPreDealdeskPrice
        : props.HRImpPreDealdeskPrice

   const quickstartListPrice = isQuickstartWithBenAdmin
      ? props.HRImpPreDealdeskPrice + props.BenAdminImpPreDealdeskPrice
      : props.HRImpPreDealdeskPrice

   // Custom function to calculate split discount for QuickStart/Ben Admin combinations
   const calculateQuickstartSplitDiscount = (discountPercent: number) => {
      if (!isQuickstartWithBenAdmin) {
         // Normal QuickStart calculation
         return {
            discountedPrice:
               props.HRImpPreDealdeskPrice * (1 - discountPercent / 100),
            showWarning: false,
         }
      }

      // Split discount calculation: full discount to QuickStart, cap Ben Admin at 25%
      const quickstartDiscountedPrice =
         props.HRImpPreDealdeskPrice * (1 - discountPercent / 100)
      const benAdminDiscountPercent = Math.min(discountPercent, 25)
      const benAdminDiscountedPrice =
         props.BenAdminImpPreDealdeskPrice * (1 - benAdminDiscountPercent / 100)

      return {
         discountedPrice: quickstartDiscountedPrice + benAdminDiscountedPrice,
         showWarning: discountPercent > 40,
      }
   }

   const validateForm = () => {
      // Validate the form inputs
      let isValid = true
      if (HRISImplementation) {
         if (HRISImplementationDiscountPercent <= 0) {
            isValid = false
            setHRISImplementationDiscountPercentValid(false)
            setHRISImplementationDiscountPercentInvalid(true)
         } else {
            setHRISImplementationDiscountPercentValid(true)
            setHRISImplementationDiscountPercentInvalid(false)
         }

         // Only validate HRIS Implementation details if discount is 40% or higher
         if (HRISImplementationDiscountPercent >= 40) {
            const hrisDetails = HRISImplementationDetails
            if (
               !hrisDetails.callRecordingLink ||
               !hrisDetails.relationshipHistory ||
               !hrisDetails.relationshipContextAns ||
               !hrisDetails.hasCombinedOffers ||
               !hrisDetails.nonStandardRequestAns ||
               !hrisDetails.dealStructureAns ||
               !hrisDetails.extraDealDeskDetailsAns
            ) {
               isValid = false
               setHRISDetailsComplete(false)
            } else {
               setHRISDetailsComplete(true)
            }
         } else {
            // For discounts less than 40%, consider details complete
            setHRISDetailsComplete(true)
         }
      }

      if (QuickStart) {
         if (QuickStartDiscountPercent <= 0) {
            isValid = false
            setQuickStartDiscountPercentValid(false)
            setQuickStartDiscountPercentInvalid(true)
         } else {
            setQuickStartDiscountPercentValid(true)
            setQuickStartDiscountPercentInvalid(false)
         }
      }

      if (PayrollImplementation) {
         if (PayrollImplementationDiscountPercent <= 0) {
            isValid = false
            setPayrollImplementationDiscountPercentValid(false)
            setPayrollImplementationDiscountPercentInvalid(true)
         } else {
            setPayrollImplementationDiscountPercentValid(true)
            setPayrollImplementationDiscountPercentInvalid(false)
         }

         // Only validate Payroll Implementation details if discount is 40% or higher
         if (PayrollImplementationDiscountPercent >= 40) {
            const payrollDetails = PayrollImplementationDetails
            if (
               !payrollDetails.callRecordingLink ||
               !payrollDetails.relationshipHistory ||
               !payrollDetails.relationshipContextAns ||
               !payrollDetails.hasCombinedOffers ||
               !payrollDetails.nonStandardRequestAns ||
               !payrollDetails.dealStructureAns ||
               !payrollDetails.extraDealDeskDetailsAns
            ) {
               isValid = false
               setPayrollDetailsComplete(false)
            } else {
               setPayrollDetailsComplete(true)
            }
         } else {
            // For discounts less than 40%, consider details complete
            setPayrollDetailsComplete(true)
         }
      }

      // Validate MRR Discount Details if MRR Discount is selected
      if (MRRDiscount) {
         if (
            !MRRDiscountDetails.callRecordingLink ||
            !MRRDiscountDetails.relationshipHistory ||
            !MRRDiscountDetails.relationshipContextAns ||
            !MRRDiscountDetails.hasCombinedOffers ||
            !MRRDiscountDetails.nonStandardRequestAns ||
            !MRRDiscountDetails.dealStructureAns ||
            !MRRDiscountDetails.extraDealDeskDetailsAns
         ) {
            isValid = false
            setMRRDiscountDetailsComplete(false)
         } else {
            setMRRDiscountDetailsComplete(true)
         }
      }

      return isValid
   }

   const handleSubmit = async (event: any) => {
      event.preventDefault()
      setFormSubmissionAttempted(true)

      if (validateForm()) {
         // Handle the submission logic here
         const toastId = toast.loading(
            'Submitting Deal Desk.\nPlease wait here and do not refresh your page.\nThis process usually takes less than 1 minute.',
            { style: { maxWidth: '60%', width: '500px' } },
         )

         if (!state) {
            toast.error(
               'QuoteContext is null. Unable to proceed.\nPlease File a ticket at Datadesk.bamboohr.net',
               {
                  id: toastId,
                  style: { maxWidth: '60%', width: '500px' },
                  duration: 5000,
               },
            )
            return
         }
         const permissionsSaveToAccount =
            state.featurePermissions['SaveQuoteToAccount']
         // Checks to validate the expiration date
         if (
            !state ||
            state.quoteExpirationDate === null ||
            state.quoteExpirationDate === ''
         ) {
            toast.error(
               `Submitting a Deal Desk requires saving the quote.\nUnable to save opportunity due to an invalid/missing expiration date.\nPlease update the quote expiration date and try again.`,
               {
                  id: toastId,
                  style: { maxWidth: '60%', width: '500px' },
                  duration: 5000,
               },
            )
            return
         } else if (
            state.opportunity_id.slice(0, 4) == 'ACC-' &&
            !permissionsSaveToAccount.includes(state.user_role)
         ) {
            toast.error(
               'Submitting a Deal Desk Requires permission to Save the quote.\nYou do not have permission to save a Quote to an Account. Please save a Quote on an Opportunity instead.',
               {
                  id: toastId,
                  style: { maxWidth: '60%', width: '500px' },
                  duration: 5000,
               },
            )
            return
         } else {
            try {
               let DealDeskLocks = state.dealDeskLocks
               if (HRISImplementation) {
                  lockDealDeskType(
                     `${props.recordType} Sale HRIS Implementation` as SFDealDeskType,
                     state.discount_list,
                     state.addDealDeskLock,
                  )
                  const HRISApplyTo = getDealDeskApplyTo(
                     `${props.recordType} Sale HRIS Implementation` as SFDealDeskType,
                     state.discount_list,
                  )?.map((item) => item.trim())
                  DealDeskLocks = new Set([...DealDeskLocks, ...HRISApplyTo])
               }
               if (QuickStart) {
                  lockDealDeskType(
                     SFDealDeskType.QuickStart,
                     state.discount_list,
                     state.addDealDeskLock,
                  )
                  const QuickStartApplyTo = getDealDeskApplyTo(
                     SFDealDeskType.QuickStart,
                     state.discount_list,
                  )?.map((item) => item.trim())
                  DealDeskLocks = new Set([
                     ...DealDeskLocks,
                     ...QuickStartApplyTo,
                  ])
               }
               if (PayrollImplementation) {
                  lockDealDeskType(
                     SFDealDeskType.PayrollSaleHRISImplementation,
                     state.discount_list,
                     state.addDealDeskLock,
                  )
                  const PayrollImpApplyTo = getDealDeskApplyTo(
                     SFDealDeskType.PayrollSaleHRISImplementation,
                     state.discount_list,
                  )?.map((item) => item.trim())
                  DealDeskLocks = new Set([
                     ...DealDeskLocks,
                     ...PayrollImpApplyTo,
                  ])
               }
               // This has to be synchronous for our locking mechanism to work properly. We pass in the locks as an optional parameter to avoid asyncrhonous race conditions.
               await saveOpportunity({
                  state: state,
                  deal_desk_locks: DealDeskLocks,
                  hasUnlimitedDatePermission: hasPermission(
                     'UnlimitedActivationDateSelect',
                     state.user_role,
                  ),
               })
            } catch (error) {
               if (error instanceof Error) {
                  if (HRISImplementation) {
                     unlockDealDeskType(
                        `${recordType} HRIS Implementation` as SFDealDeskType,
                        state.discount_list,
                        state.removeDealDeskLock,
                     )
                  }
                  if (QuickStart) {
                     unlockDealDeskType(
                        SFDealDeskType.QuickStart,
                        state.discount_list,
                        state.removeDealDeskLock,
                     )
                  }
                  if (PayrollImplementation) {
                     unlockDealDeskType(
                        SFDealDeskType.PayrollSaleHRISImplementation,
                        state.discount_list,
                        state.removeDealDeskLock,
                     )
                  }
                  console.error('Error saving opportunity:', error.message)
                  toast.error(
                     `Submitting a Deal Desk Requires permission to Save the quote.\n${error.message || 'An error occurred while saving the opportunity'}`,
                     {
                        id: toastId,
                        style: { maxWidth: '60%', width: '500px' },
                        duration: 5000,
                     },
                  )
               } else if (typeof error === 'string') {
                  console.error('Error saving opportunity:', error)
                  toast.error(
                     `Submitting a Deal Desk Requires permission to Save the quote.\n${error || 'An error occurred while saving the opportunity'}`,
                     {
                        id: toastId,
                        style: { maxWidth: '60%', width: '500px' },
                        duration: 5000,
                     },
                  )
               } else {
                  console.error('Error saving opportunity:', error)
                  toast.error(
                     `Submitting a Deal Desk Requires permission to Save the quote.\nAn unknown error occurred while saving the opportunity`,
                     {
                        id: toastId,
                        style: { maxWidth: '60%', width: '500px' },
                        duration: 5000,
                     },
                  )
               }
            }
         }
         // Call the API to submit the deal desk
         try {
            const res = await SubmitDealDesk(
               props.recordType,
               props.opportunity_id,
               HRISImplementation,
               HRISImplementationDiscountPercent,
               HRISImplementationDetails,
               QuickStart,
               QuickStartDiscountPercent,
               QuickStartDetails,
               QuickStartQualifier,
               PayrollImplementation,
               PayrollImplementationDiscountPercent,
               PayrollImplementationDetails,
               MRRDiscount,
               MRRDiscountDetails,
               isBenAdminOnly,
               props.termLength,
               state.prepayLength,
               state.monthlyTotal?.MRRTotalUSD ?? 0,
            )
            if (res.ok) {
               toast.success('Deal desk submitted successfully!', {
                  id: toastId,
                  style: { maxWidth: '60%', width: '500px' },
               })
               handleClose()
            } else {
               const responseData = await res.json()
               if (!responseData.message) {
                  toast.error(
                     'An unexpected error occurred. No message found in the response.',
                     {
                        id: toastId,
                        style: { maxWidth: '60%', width: '500px' },
                        duration: 5000,
                     },
                  )
                  return
               }
               try {
                  //TODO: parse response to unlock frontend deal desks maybe?
                  const message = JSON.parse(responseData.message)
                  const response = message.response
                  const approvalSentSuccesses = translateTypes(
                     message.approvalSentSuccesses.replace(/,+$/, ''),
                  )
                  const dealDeskCreationFailures = translateTypes(
                     message.dealDeskCreationFailures.replace(/,+$/, ''),
                  )
                  const approvalSentFailures = translateTypes(
                     message.approvalSentFailures.replace(/,+$/, ''),
                  )

                  const dealDeskCreationSuccesses = translateTypes(
                     message.dealDeskCreationSuccesses.replace(/,+$/, ''),
                  )
                  const combinedFailures = [
                     ...new Set([
                        ...dealDeskCreationFailures.split(','),
                        ...approvalSentFailures.split(','),
                     ]),
                  ].join(', ')
                  const alreadyOpen = translateTypes(
                     message.alreadyOpen.replace(/,+$/, ''),
                  )
                  let errorMessage = response
                  if (dealDeskCreationSuccesses) {
                     errorMessage += `\nDeal Desk Creation Successes: ${dealDeskCreationSuccesses}`
                  }
                  if (alreadyOpen) {
                     errorMessage += `\nAlready Open: ${alreadyOpen}`
                     if (!combinedFailures) {
                        errorMessage += `\nYou already have a deal desk open for this product.\nIf you want to resubmit with a different percentage,\nplease close the existing deal desk first.`
                     }
                  }
                  if (combinedFailures) {
                     errorMessage += `\nDeal Desk Creation Failures: ${combinedFailures}`
                     //TODO: automatically refresh?
                     errorMessage += `\nSome Deal Desk records failed to create.\nPlease file a ticket at Datadesk.bamboohr.net\nRefresh your page to allow selecting discounts again.`
                  }
                  toast.error(
                     (t) => (
                        <div
                           className='modal show'
                           style={{ display: 'block', position: 'initial' }}
                        >
                           <Modal.Dialog>
                              <Modal.Header
                                 closeButton
                                 onClick={() => toast.dismiss(t.id)}
                              >
                                 <Modal.Title>
                                    Deal Desk Submission Error
                                 </Modal.Title>
                              </Modal.Header>
                              <Modal.Body>
                                 <p>{errorMessage || res.statusText}</p>
                              </Modal.Body>
                           </Modal.Dialog>
                        </div>
                     ),
                     {
                        id: toastId,
                        style: { maxWidth: '60%', width: '500px' },
                        duration: Infinity,
                     },
                  )
               } catch (error) {
                  if (responseData.message) {
                     console.error(responseData.message)
                     toast.error(responseData.message, {
                        id: toastId,
                        style: { maxWidth: '60%', width: '500px' },
                     })
                  } else if (error instanceof Error) {
                     console.error('Error submitting deal desk:', error)
                     toast.error(
                        `Error submitting deal desk: ${error.message}`,
                        {
                           id: toastId,
                           style: { maxWidth: '60%', width: '500px' },
                        },
                     )
                  } else if (typeof error === 'string') {
                     console.error('Error submitting deal desk:', error)
                     toast.error(error, {
                        id: toastId,
                        style: { maxWidth: '60%', width: '500px' },
                     })
                  } else {
                     console.error('Error submitting deal desk:', error)
                     toast.error(
                        `Error submitting deal desk: An unknown error occurred`,
                        {
                           id: toastId,
                           style: { maxWidth: '60%', width: '500px' },
                        },
                     )
                  }
               }
            }
         } catch (error) {
            if (error instanceof Error) {
               console.error('Error submitting deal desk:', error)
               toast.error(`Error submitting deal desk: ${error.message}`, {
                  id: toastId,
                  style: { maxWidth: '60%', width: '500px' },
               })
            } else if (typeof error === 'string') {
               console.error('Error submitting deal desk:', error)
               toast.error(`Error submitting deal desk: ${error}`, {
                  id: toastId,
                  style: { maxWidth: '60%', width: '500px' },
               })
            } else {
               console.error('Error submitting deal desk:', error)
               toast.error(
                  `Error submitting deal desk: An unknown error occurred`,
                  { id: toastId, style: { maxWidth: '60%', width: '500px' } },
               )
            }
         }
      } else {
         return
      }
   }

   const handleClose = () => {
      setShow(false)
      setHRISImplementation(false)
      setQuickStart(false)
      setPayrollImplementation(false)
      setHRISImplementationDiscountPercent(0)
      setQuickStartDiscountPercent(0)
      setQuickStartQualifier(false)
      setPayrollImplementationDiscountPercent(0)

      // Reset deal desk details
      setHRISImplementationDetails({
         callRecordingLink: '',
         relationshipHistory: '',
         relationshipContextAns: '',
         hasCombinedOffers: '',
         nonStandardRequestAns: '',
         dealStructureAns: '',
         extraDealDeskDetailsAns: '',
         additionalInformation: '',
         termLength: 0,
         prepayLength: 0,
      })

      setPayrollImplementationDetails({
         callRecordingLink: '',
         relationshipHistory: '',
         relationshipContextAns: '',
         hasCombinedOffers: '',
         nonStandardRequestAns: '',
         dealStructureAns: '',
         extraDealDeskDetailsAns: '',
         additionalInformation: '',
         termLength: 0,
         prepayLength: 0,
      })

      setHRISImplementationDiscountPercentValid(false)
      setQuickStartDiscountPercentValid(false)
      setPayrollImplementationDiscountPercentValid(false)
      setHRISImplementationDiscountPercentInvalid(false)
      setQuickStartDiscountPercentInvalid(false)
      setPayrollImplementationDiscountPercentInvalid(false)

      // Reset details completion status
      setHRISDetailsComplete(false)
      setPayrollDetailsComplete(false)

      setSubmitDisabled(true)
      setFormSubmissionAttempted(false)
   }
   const handleShow = () => setShow(true)
   const [
      HRISImplementationDiscountPercentValid,
      setHRISImplementationDiscountPercentValid,
   ] = useState(false)
   const [SubmitDisabled, setSubmitDisabled] = useState(true)
   const [QuickStartDiscountPercentValid, setQuickStartDiscountPercentValid] =
      useState(false)
   const [
      PayrollImplementationDiscountPercentValid,
      setPayrollImplementationDiscountPercentValid,
   ] = useState(false)
   const [
      HRISImplementationDiscountPercentInvalid,
      setHRISImplementationDiscountPercentInvalid,
   ] = useState(false)
   const [
      QuickStartDiscountPercentInvalid,
      setQuickStartDiscountPercentInvalid,
   ] = useState(false)
   const [
      PayrollImplementationDiscountPercentInvalid,
      setPayrollImplementationDiscountPercentInvalid,
   ] = useState(false)

   // Deal desk details state
   const [HRISImplementationDetails, setHRISImplementationDetails] =
      useState<DealDeskDetails>({
         callRecordingLink: '',
         relationshipHistory: '',
         relationshipContextAns: '',
         hasCombinedOffers: '',
         nonStandardRequestAns: '',
         dealStructureAns: '',
         extraDealDeskDetailsAns: '',
         additionalInformation: '',
         termLength: props.termLength,
         prepayLength: state.prepayLength,
      })

   const [PayrollImplementationDetails, setPayrollImplementationDetails] =
      useState<DealDeskDetails>({
         callRecordingLink: '',
         relationshipHistory: '',
         relationshipContextAns: '',
         hasCombinedOffers: '',
         nonStandardRequestAns: '',
         dealStructureAns: '',
         extraDealDeskDetailsAns: '',
         additionalInformation: '',
         termLength: props.termLength,
         prepayLength: state.prepayLength,
      })

   const [MRRDiscountDetails, setMRRDiscountDetails] =
      useState<DealDeskDetails>({
         callRecordingLink: '',
         relationshipHistory: '',
         relationshipContextAns: '',
         hasCombinedOffers: '',
         nonStandardRequestAns: '',
         dealStructureAns: '',
         extraDealDeskDetailsAns: '',
         additionalInformation: '',
         termLength: props.termLength,
         prepayLength: state.prepayLength,
      })

   // State for tracking completion of details
   const [HRISDetailsComplete, setHRISDetailsComplete] = useState(false)
   const [PayrollDetailsComplete, setPayrollDetailsComplete] = useState(false)
   const [MRRDiscountDetailsComplete, setMRRDiscountDetailsComplete] =
      useState(false)

   // State for showing sub-modals
   const [showHRISDetailsModal, setShowHRISDetailsModal] =
      useState<boolean>(false)
   const [showPayrollDetailsModal, setShowPayrollDetailsModal] =
      useState<boolean>(false)
   const [showMRRDiscountModal, setShowMRRDiscountModal] =
      useState<boolean>(false)

   const [HRISImplementation, setHRISImplementation] = useState<boolean>(false)
   const [QuickStart, setQuickStart] = useState<boolean>(false)
   const [PayrollImplementation, setPayrollImplementation] =
      useState<boolean>(false)
   const [MRRDiscount, setMRRDiscount] = useState<boolean>(false)

   // Discount percentage state variables
   const [
      HRISImplementationDiscountPercent,
      setHRISImplementationDiscountPercent,
   ] = useState(0)
   const onHRISImpDiscountPercentChange = (value: number) => {
      setHRISImplementationDiscountPercent(Math.min(value, 100))
   }
   const [QuickStartDiscountPercent, setQuickStartDiscountPercent] = useState(0)
   const onQuickStartDiscountPercentChange = (value: number) => {
      setQuickStartDiscountPercent(Math.min(value, 100))
   }
   const [QuickStartQualifier, setQuickStartQualifier] = useState(false)
   const [QuickStartDetails, setQuickStartDetails] = useState<DealDeskDetails>({
      callRecordingLink: '',
      relationshipHistory: '',
      relationshipContextAns: '',
      hasCombinedOffers: '',
      nonStandardRequestAns: '',
      dealStructureAns: '',
      extraDealDeskDetailsAns: '',
      additionalInformation: '',
      termLength: props.termLength,
      prepayLength: state.prepayLength,
   })
   const [QuickStartCommentsValid, setQuickStartCommentsValid] = useState(false)
   const [QuickStartCommentsInvalid, setQuickStartCommentsInvalid] =
      useState(false)

   const [
      PayrollImplementationDiscountPercent,
      setPayrollImplementationDiscountPercent,
   ] = useState(0)
   const onPayrollImpDiscountPercentChange = (value: number) => {
      setPayrollImplementationDiscountPercent(Math.min(value, 100))
   }

   useEffect(() => {
      if (
         HRISImplementation ||
         QuickStart ||
         PayrollImplementation ||
         MRRDiscount
      ) {
         setSubmitDisabled(false)
      } else {
         setSubmitDisabled(true)
      }
   }, [HRISImplementation, QuickStart, PayrollImplementation, MRRDiscount])

   useEffect(() => {
      let isInvalid = false

      // Check discount percentage validation
      if (
         HRISImplementationDiscountPercentInvalid ||
         QuickStartDiscountPercentInvalid ||
         PayrollImplementationDiscountPercentInvalid
      ) {
         isInvalid = true
      }

      // Check HRIS Implementation validation based on discount percentage
      if (HRISImplementation) {
         if (HRISImplementationDiscountPercent >= 40) {
            // For discounts >= 40%, require details completion
            if (!HRISDetailsComplete) {
               isInvalid = true
            }
         } else {
            // For discounts < 40%, require additional information
            if (!HRISImplementationDetails.additionalInformation) {
               isInvalid = true
            }
         }
      }

      // Check Payroll Implementation validation based on discount percentage
      if (PayrollImplementation) {
         if (PayrollImplementationDiscountPercent >= 40) {
            // For discounts >= 40%, require details completion
            if (!PayrollDetailsComplete) {
               isInvalid = true
            }
         } else {
            // For discounts < 40%, require additional information
            if (!PayrollImplementationDetails.additionalInformation) {
               isInvalid = true
            }
         }
      }

      // Check MRR Discount has all the details if selected
      if (MRRDiscount) {
         if (!MRRDiscountDetailsComplete) {
            isInvalid = true
         }
      }

      setSubmitDisabled(isInvalid)
   }, [
      HRISImplementationDiscountPercentInvalid,
      QuickStartDiscountPercentInvalid,
      PayrollImplementationDiscountPercentInvalid,
      HRISImplementation,
      PayrollImplementation,
      MRRDiscount,
      HRISDetailsComplete,
      PayrollDetailsComplete,
      MRRDiscountDetailsComplete,
      HRISImplementationDiscountPercent,
      PayrollImplementationDiscountPercent,
      HRISImplementationDetails.additionalInformation,
      PayrollImplementationDetails.additionalInformation,
   ])

   // Determine if modal should be smaller (when Additional Information is hidden for all selected deal desk types)
   const shouldUseSmallModal = () => {
      const hrisUsesDetails =
         HRISImplementation && HRISImplementationDiscountPercent >= 40
      const payrollUsesDetails =
         PayrollImplementation && PayrollImplementationDiscountPercent >= 40
      const quickStartSelected = QuickStart

      // If only QuickStart is selected, or if all selected deal desk types use Details buttons, use smaller modal
      if (quickStartSelected && !HRISImplementation && !PayrollImplementation) {
         return true // Only QuickStart selected
      }

      if (
         HRISImplementation &&
         hrisUsesDetails &&
         PayrollImplementation &&
         payrollUsesDetails
      ) {
         return true // Both HRIS and Payroll use Details buttons
      }

      if (HRISImplementation && !PayrollImplementation && hrisUsesDetails) {
         return true // Only HRIS selected and uses Details button
      }

      if (PayrollImplementation && !HRISImplementation && payrollUsesDetails) {
         return true // Only Payroll selected and uses Details button
      }

      return false // At least one deal desk type shows Additional Information in main modal
   }

   return (
      <>
         <Button
            onClick={handleShow}
            className={props.buttonClass}
            size={props.buttonSize}
         >
            {getString('product.DealdeskModalButton.ButtonText')}
         </Button>

         <Modal
            show={show}
            onHide={handleClose}
            centered={true}
            size={shouldUseSmallModal() ? 'lg' : 'xl'}
         >
            <Modal.Header>
               <Modal.Title className='w-100 text-center'>
                  {getString('product.DealdeskModalButton.Modal.Title')}
               </Modal.Title>
            </Modal.Header>
            <Form noValidate onSubmit={handleSubmit}>
               <Modal.Body>
                  <b>
                     {getString(
                        'product.DealdeskModalButton.Modal.Body.Question',
                     )}
                  </b>
                  <div className='justify-content-left'>
                     {props.HRISImpSelected && (
                        <DealDeskType
                           order={1}
                           label={hrisImplementationLabel}
                           disabled={props.disableHRISImp}
                           getFormattedString={getFormattedString}
                           checked={HRISImplementation}
                           setChecked={setHRISImplementation}
                           disablingDiscounts={
                              props.HRISDisablingDiscountList || []
                           }
                        />
                     )}
                     {props.QuickStartSelected && (
                        <DealDeskType
                           order={2}
                           label={quickstartLabel}
                           disabled={props.disableQuickStart}
                           getFormattedString={getFormattedString}
                           checked={QuickStart}
                           setChecked={setQuickStart}
                           disablingDiscounts={
                              props.HRISDisablingDiscountList || []
                           }
                        />
                     )}
                     {props.PayrollImpSelected && (
                        <DealDeskType
                           order={3}
                           label='Payroll Implementation/Foundations'
                           disabled={props.disablePayrollImp}
                           getFormattedString={getFormattedString}
                           checked={PayrollImplementation}
                           setChecked={setPayrollImplementation}
                           disablingDiscounts={
                              props.payrollDisablingDiscountList || []
                           }
                        />
                     )}
                     {props.MRRSelected &&
                        state.featurePermissions['MRRDiscountFlag'].includes(
                           state.user_role,
                        ) && (
                           <DealDeskType
                              order={4}
                              label='MRR Discount'
                              disabled={!props.MRRSelected}
                              getFormattedString={getFormattedString}
                              checked={MRRDiscount}
                              setChecked={setMRRDiscount}
                              disablingDiscounts={[]}
                           />
                        )}
                  </div>
                  {(HRISImplementation ||
                     QuickStart ||
                     PayrollImplementation ||
                     MRRDiscount) && <hr className='bhr1'></hr>}
                  {HRISImplementation && (
                     <div>
                        {HRISImplementationDiscountPercent >= 40 ? (
                           // Show Details button for discounts >= 40%
                           <>
                              <div className='mb-2'>
                                 <b>{hrisImplementationLabel}:</b>
                              </div>
                              <DealDeskPercentInputWithoutComments
                                 order={1}
                                 className=''
                                 label=''
                                 listPriceLabel={getString(
                                    'global.OneTimeCharge',
                                 )}
                                 discountLabel={
                                    isHRISFullWithBenAdmin
                                       ? getString(
                                            'product.DealdeskModalButton.Modal.DealDeskPercentInputWithoutComments.DiscountLabel.isHRISFullWithBenAdmin',
                                         )
                                       : isBenAdminOnly
                                         ? getString(
                                              'product.DealdeskModalButton.Modal.DealDeskPercentInputWithoutComments.DiscountLabel.isBenAdminOnly',
                                           )
                                         : getString(
                                              'product.DealdeskModalButton.Modal.DealDeskPercentInputWithoutComments.DiscountLabel.isHrImpOnly',
                                           )
                                 }
                                 discountPercent={
                                    HRISImplementationDiscountPercent
                                 }
                                 setDiscountPercent={
                                    onHRISImpDiscountPercentChange
                                 }
                                 listPrice={hrImplementationListPrice}
                                 currencyObject={props.currencyObject}
                                 discountPriceLabel={
                                    isHRISFullWithBenAdmin
                                       ? getString(
                                            'product.DealdeskModalButton.Modal.DealDeskPercentInputWithoutComments.DiscountPriceLabel.isHRISFullWithBenAdmin',
                                         )
                                       : isBenAdminOnly
                                         ? getString(
                                              'product.DealdeskModalButton.Modal.DealDeskPercentInputWithoutComments.DiscountPriceLabel.isBenAdminOnly',
                                           )
                                         : getString('HRIS Imp. with Discount')
                                 }
                                 discountPercentValid={
                                    HRISImplementationDiscountPercentValid
                                 }
                                 setValidFunction={
                                    setHRISImplementationDiscountPercentValid
                                 }
                                 discountPercentInvalid={
                                    HRISImplementationDiscountPercentInvalid
                                 }
                                 setInvalidFunction={
                                    setHRISImplementationDiscountPercentInvalid
                                 }
                                 getString={getString}
                              />
                              <div className='mt-2 d-flex justify-content-start'>
                                 <Button
                                    variant='outline-primary'
                                    size='sm'
                                    onClick={() =>
                                       setShowHRISDetailsModal(true)
                                    }
                                    className='me-2'
                                 >
                                    <div
                                       dangerouslySetInnerHTML={{
                                          __html: getString(
                                             HRISDetailsComplete
                                                ? 'product.DealdeskModalButton.Modal.DetailsButton'
                                                : 'product.DealdeskModalButton.Modal.DetailsButton.Warning',
                                          ),
                                       }}
                                    />
                                 </Button>
                                 {!HRISDetailsComplete && (
                                    <small className='text-danger align-self-center'>
                                       {getString(
                                          'product.DealdeskModalButton.Modal.DealDeskPercentInputWithoutComments.WarningText',
                                       )}
                                    </small>
                                 )}
                              </div>
                           </>
                        ) : (
                           // Show Additional Info field for discounts < 40%
                           <DealDeskPercentInput
                              order={1}
                              className=''
                              label={`${hrisImplementationLabel}:`}
                              listPriceLabel={getString('global.OneTimeCharge')}
                              discountLabel={
                                 isHRISFullWithBenAdmin
                                    ? getString(
                                         'product.DealdeskModalButton.Modal.DealDeskPercentInputWithoutComments.DiscountLabel.isHRISFullWithBenAdmin',
                                      )
                                    : isBenAdminOnly
                                      ? getString(
                                           'product.DealdeskModalButton.Modal.DealDeskPercentInputWithoutComments.DiscountLabel.isBenAdminOnly',
                                        )
                                      : getString(
                                           'product.DealdeskModalButton.Modal.DealDeskPercentInputWithoutComments.DiscountLabel.isHrImpOnly',
                                        )
                              }
                              discountPercent={
                                 HRISImplementationDiscountPercent
                              }
                              setDiscountPercent={
                                 onHRISImpDiscountPercentChange
                              }
                              comments={
                                 HRISImplementationDetails.additionalInformation
                              }
                              commentsValid={
                                 formSubmissionAttempted
                                    ? !!HRISImplementationDetails.additionalInformation
                                    : true
                              }
                              commentsInvalid={
                                 formSubmissionAttempted
                                    ? !HRISImplementationDetails.additionalInformation
                                    : false
                              }
                              setCommentsValidFunction={() => {}}
                              setCommentsInvalidFunction={() => {}}
                              setComments={(value) =>
                                 setHRISImplementationDetails((prev) => ({
                                    ...prev,
                                    additionalInformation: value,
                                 }))
                              }
                              listPrice={hrImplementationListPrice}
                              currencyObject={props.currencyObject}
                              discountPriceLabel={
                                 isHRISFullWithBenAdmin
                                    ? getString(
                                         'product.DealdeskModalButton.Modal.DealDeskPercentInputWithoutComments.DiscountPriceLabel.isHRISFullWithBenAdmin',
                                      )
                                    : isBenAdminOnly
                                      ? getString(
                                           'product.DealdeskModalButton.Modal.DealDeskPercentInputWithoutComments.DiscountPriceLabel.isBenAdminOnly',
                                        )
                                      : getString(
                                           'product.DealdeskModalButton.Modal.DealDeskPercentInputWithoutComments.DiscountPriceLabel.isHrImpOnly',
                                        )
                              }
                              discountPercentValid={
                                 HRISImplementationDiscountPercentValid
                              }
                              setValidFunction={
                                 setHRISImplementationDiscountPercentValid
                              }
                              discountPercentInvalid={
                                 HRISImplementationDiscountPercentInvalid
                              }
                              setInvalidFunction={
                                 setHRISImplementationDiscountPercentInvalid
                              }
                              getString={getString}
                           />
                        )}
                     </div>
                  )}

                  {QuickStart && (
                     <div className={HRISImplementation ? 'mt-2' : ''}>
                        <b>{quickstartLabel}:</b>
                        <div className=''>
                           <div className='row' style={{ order: 2 }}>
                              <div className='col-bhr1'>
                                 <InputGroup size='sm' className='mb-1'>
                                    <InputGroup.Text>
                                       {isQuickstartWithBenAdmin
                                          ? getString(
                                               'product.DealdeskModalButton.Modal.Quickstart.DiscountLabel.isQuickstartWithBenAdmin',
                                            )
                                          : getString(
                                               'product.DealdeskModalButton.Modal.Quickstart.DiscountLabel.isQuickstartOnly',
                                            )}
                                    </InputGroup.Text>
                                    <Form.Control
                                       style={{ textAlign: 'right' }}
                                       aria-label='Requested Discount Percentage'
                                       type='number'
                                       value={
                                          QuickStartDiscountPercent <= 0
                                             ? ''
                                             : Math.min(
                                                  QuickStartDiscountPercent,
                                                  100,
                                               ).toString()
                                       }
                                       onChange={(e) =>
                                          onPercentInputChange(
                                             Number(e.target.value),
                                             QuickStartDiscountPercent,
                                             onQuickStartDiscountPercentChange,
                                             QuickStartDiscountPercentInvalid,
                                             QuickStartDiscountPercentValid,
                                             setQuickStartDiscountPercentValid,
                                             setQuickStartDiscountPercentInvalid,
                                          )
                                       }
                                       isValid={QuickStartDiscountPercentValid}
                                       isInvalid={
                                          QuickStartDiscountPercentInvalid
                                       }
                                       min={0}
                                       max={100}
                                       placeholder='0'
                                    />
                                    <InputGroup.Text>%</InputGroup.Text>
                                    <Form.Control.Feedback type='invalid'>
                                       {getString(
                                          'product.DealdeskModalButton.Modal.Quickstart.Feedback',
                                       )}
                                    </Form.Control.Feedback>
                                 </InputGroup>
                                 <DealDeskPricing
                                    listPrice={quickstartListPrice}
                                    discountPercent={QuickStartDiscountPercent}
                                    currencyObject={props.currencyObject}
                                    discountPriceLabel={
                                       isQuickstartWithBenAdmin
                                          ? getString(
                                               'product.DealdeskModalButton.Modal.Quickstart.DiscountPriceLabel.isQuickstartWithBenAdmin',
                                            )
                                          : getString(
                                               'product.DealdeskModalButton.Modal.Quickstart.DiscountPriceLabel.isQuickstartOnly',
                                            )
                                    }
                                    listPriceLabel={getString(
                                       'global.OneTimeCharge',
                                    )}
                                    customDiscountedPrice={
                                       isQuickstartWithBenAdmin
                                          ? calculateQuickstartSplitDiscount(
                                               QuickStartDiscountPercent,
                                            ).discountedPrice
                                          : undefined
                                    }
                                 />
                              </div>
                              <DealDeskCommentInput
                                 comments={
                                    QuickStartDetails.additionalInformation
                                 }
                                 commentsValid={QuickStartCommentsValid}
                                 commentsInvalid={QuickStartCommentsInvalid}
                                 setCommentsValidFunction={() => {}}
                                 setCommentsInvalidFunction={() => {}}
                                 setComments={(value) =>
                                    setQuickStartDetails((prev) => ({
                                       ...prev,
                                       additionalInformation: value,
                                    }))
                                 }
                                 getString={getString}
                              />
                           </div>
                        </div>
                        {/* Warning text for Ben Admin discount cap in QuickStart/Ben Admin combinations */}
                        {isQuickstartWithBenAdmin &&
                           QuickStartDiscountPercent > 40 && (
                              <div className='mt-2'>
                                 <small
                                    className='text-danger'
                                    style={{ fontSize: 'inherit' }}
                                 >
                                    {getString(
                                       'product.DealdeskModalButton.Modal.Quickstart.CapDisclaimer',
                                    )}
                                 </small>
                              </div>
                           )}
                     </div>
                  )}
                  {QuickStart && QuickStartDiscountPercent >= 50 && (
                     <div className='mt-2'>
                        <InputGroup size='sm' style={{ height: '3em' }}>
                           <InputGroup.Text
                              style={{
                                 width: '93%',
                                 whiteSpace: 'normal',
                                 wordWrap: 'break-word',
                              }}
                           >
                              {getString(
                                 'product.DealdeskModalButton.Modal.Quickstart.QuickStartDiscountOver50',
                              )}
                           </InputGroup.Text>
                           <Dropdown>
                              <Dropdown.Toggle
                                 variant='outline-secondary'
                                 id='QuickStart-qualifier'
                                 style={{ width: '7%' }}
                              >
                                 {QuickStartQualifier
                                    ? getString('global.Yes')
                                    : getString('global.No')}
                              </Dropdown.Toggle>
                              <Dropdown.Menu>
                                 <Dropdown.Item
                                    onClick={() => setQuickStartQualifier(true)}
                                 >
                                    {getString('global.Yes')}
                                 </Dropdown.Item>
                                 <Dropdown.Item
                                    onClick={() =>
                                       setQuickStartQualifier(false)
                                    }
                                 >
                                    {getString('global.No')}
                                 </Dropdown.Item>
                              </Dropdown.Menu>
                           </Dropdown>
                        </InputGroup>
                     </div>
                  )}

                  {PayrollImplementation && (
                     <div
                        className={
                           HRISImplementation || QuickStart ? 'mt-2' : ''
                        }
                     >
                        {PayrollImplementationDiscountPercent >= 40 ? (
                           // Show Details button for discounts >= 40%
                           <>
                              <div className='mb-2'>
                                 <b>
                                    {getString(
                                       'product.DealdeskModalButton.Modal.PayrollImplementation.Label',
                                    )}
                                 </b>
                              </div>
                              <DealDeskPercentInputWithoutComments
                                 order={3}
                                 className=''
                                 label=''
                                 listPriceLabel={getString(
                                    'global.OneTimeCharge',
                                 )}
                                 discountLabel={getString(
                                    'product.DealdeskModalButton.Modal.PayrollImplementation.DiscountLabel',
                                 )}
                                 discountPercent={
                                    PayrollImplementationDiscountPercent
                                 }
                                 setDiscountPercent={
                                    onPayrollImpDiscountPercentChange
                                 }
                                 listPrice={props.PayrollPreDealdeskPrice}
                                 currencyObject={props.currencyObject}
                                 discountPriceLabel={getString(
                                    'product.DealdeskModalButton.Modal.PayrollImplementation.DiscountPriceLabel',
                                 )}
                                 discountPercentValid={
                                    PayrollImplementationDiscountPercentValid
                                 }
                                 setValidFunction={
                                    setPayrollImplementationDiscountPercentValid
                                 }
                                 discountPercentInvalid={
                                    PayrollImplementationDiscountPercentInvalid
                                 }
                                 setInvalidFunction={
                                    setPayrollImplementationDiscountPercentInvalid
                                 }
                                 getString={getString}
                              />
                              <div className='mt-2 d-flex justify-content-start'>
                                 <Button
                                    variant='outline-primary'
                                    size='sm'
                                    onClick={() =>
                                       setShowPayrollDetailsModal(true)
                                    }
                                    className='me-2'
                                 >
                                    <div
                                       dangerouslySetInnerHTML={{
                                          __html: getString(
                                             PayrollDetailsComplete
                                                ? 'product.DealdeskModalButton.Modal.DetailsButton'
                                                : 'product.DealdeskModalButton.Modal.DetailsButton.Warning',
                                          ),
                                       }}
                                    />
                                 </Button>
                                 {!PayrollDetailsComplete && (
                                    <small className='text-danger align-self-center'>
                                       {getString(
                                          'product.DealdeskModalButton.Modal.PayrollImplementation.WarningText',
                                       )}
                                    </small>
                                 )}
                              </div>
                           </>
                        ) : (
                           // Show Additional Info field for discounts < 40%
                           <DealDeskPercentInput
                              order={3}
                              className=''
                              label={getString(
                                 'product.DealdeskModalButton.Modal.PayrollImplementation.Label',
                              )}
                              listPriceLabel={getString('global.OneTimeCharge')}
                              discountLabel={getString(
                                 'product.DealdeskModalButton.Modal.PayrollImplementation.DiscountLabel',
                              )}
                              discountPercent={
                                 PayrollImplementationDiscountPercent
                              }
                              setDiscountPercent={
                                 onPayrollImpDiscountPercentChange
                              }
                              comments={
                                 PayrollImplementationDetails.additionalInformation
                              }
                              commentsValid={
                                 formSubmissionAttempted
                                    ? !!PayrollImplementationDetails.additionalInformation
                                    : true
                              }
                              commentsInvalid={
                                 formSubmissionAttempted
                                    ? !PayrollImplementationDetails.additionalInformation
                                    : false
                              }
                              setCommentsValidFunction={() => {}}
                              setCommentsInvalidFunction={() => {}}
                              setComments={(value) =>
                                 setPayrollImplementationDetails((prev) => ({
                                    ...prev,
                                    additionalInformation: value,
                                 }))
                              }
                              listPrice={props.PayrollPreDealdeskPrice}
                              currencyObject={props.currencyObject}
                              discountPriceLabel={getString(
                                 'product.DealdeskModalButton.Modal.PayrollImplementation.DiscountPriceLabel',
                              )}
                              discountPercentValid={
                                 PayrollImplementationDiscountPercentValid
                              }
                              setValidFunction={
                                 setPayrollImplementationDiscountPercentValid
                              }
                              discountPercentInvalid={
                                 PayrollImplementationDiscountPercentInvalid
                              }
                              setInvalidFunction={
                                 setPayrollImplementationDiscountPercentInvalid
                              }
                              getString={getString}
                           />
                        )}
                     </div>
                  )}

                  {MRRDiscount && (
                     <div
                        className={
                           HRISImplementation ||
                           QuickStart ||
                           PayrollImplementation
                              ? 'mt-2'
                              : ''
                        }
                     >
                        <>
                           <div className='mb-2'>
                              <b>
                                 {getString(
                                    'product.DealdeskModalButton.Modal.MRRDiscount.Label',
                                 )}
                              </b>
                           </div>
                           <div className='mt-2 d-flex justify-content-start'>
                              <Button
                                 variant='outline-primary'
                                 size='sm'
                                 onClick={() => setShowMRRDiscountModal(true)}
                                 className='me-2'
                              >
                                 <div
                                    dangerouslySetInnerHTML={{
                                       __html: getString(
                                          MRRDiscountDetailsComplete
                                             ? 'product.DealdeskModalButton.Modal.DetailsButton'
                                             : 'product.DealdeskModalButton.Modal.DetailsButton.Warning',
                                       ),
                                    }}
                                 />
                              </Button>
                              {!MRRDiscountDetailsComplete && (
                                 <small className='text-danger align-self-center'>
                                    {getString(
                                       'product.DealdeskModalButton.Modal.PayrollImplementation.WarningText',
                                    )}
                                 </small>
                              )}
                           </div>
                        </>
                     </div>
                  )}
               </Modal.Body>
               <Modal.Footer>
                  <Button variant='secondary' onClick={handleClose}>
                     {getString('global.Cancel')}
                  </Button>
                  <Button
                     variant='primary'
                     disabled={
                        SubmitDisabled ||
                        !(
                           HRISImplementation ||
                           QuickStart ||
                           PayrollImplementation ||
                           MRRDiscount
                        )
                     }
                     type='submit'
                  >
                     {getString('global.Submit')}
                  </Button>
               </Modal.Footer>
            </Form>
         </Modal>

         {/* HRIS Implementation Details Modal */}
         <DealDeskDetailsModal
            show={showHRISDetailsModal}
            onHide={() => setShowHRISDetailsModal(false)}
            title={getString(
               'product.DealdeskModalButton.Modal.DealDeskDetailsModal.HRISDetails',
            )}
            details={HRISImplementationDetails}
            setDetails={setHRISImplementationDetails}
            onComplete={() => setHRISDetailsComplete(true)}
            getString={getString}
         />

         {/* Payroll Implementation Details Modal */}
         <DealDeskDetailsModal
            show={showPayrollDetailsModal}
            onHide={() => setShowPayrollDetailsModal(false)}
            title={getString(
               'product.DealdeskModalButton.Modal.DealDeskDetailsModal.PayrollDetails',
            )}
            details={PayrollImplementationDetails}
            setDetails={setPayrollImplementationDetails}
            onComplete={() => setPayrollDetailsComplete(true)}
            getString={getString}
         />

         {/* MRR Discount Details Modal */}
         <DealDeskDetailsModal
            show={showMRRDiscountModal}
            onHide={() => setShowMRRDiscountModal(false)}
            title={getString(
               'product.DealdeskModalButton.Modal.DealDeskDetailsModal.MRRDiscountDetails',
            )}
            details={MRRDiscountDetails}
            setDetails={setMRRDiscountDetails}
            onComplete={() => setMRRDiscountDetailsComplete(true)}
            getString={getString}
         />
      </>
   )
}

// DealDeskDetailsModal Props
interface DealDeskDetailsModalProps {
   show: boolean
   onHide: () => void
   title: string
   details: DealDeskDetails
   setDetails: (details: DealDeskDetails) => void
   onComplete: () => void
   getString: (key: string, fallback?: string) => string
}

// DealDeskDetailsModal Component
function DealDeskDetailsModal(props: DealDeskDetailsModalProps) {
   const [localDetails, setLocalDetails] = useState<DealDeskDetails>(
      props.details,
   )
   const [isValid, setIsValid] = useState(false)
   const [showValidation, setShowValidation] = useState(false)

   useEffect(() => {
      setLocalDetails(props.details)
      setShowValidation(false) // Reset validation when modal opens
   }, [props.details])

   useEffect(() => {
      const valid =
         localDetails.callRecordingLink &&
         localDetails.relationshipHistory &&
         localDetails.relationshipContextAns &&
         localDetails.hasCombinedOffers &&
         localDetails.nonStandardRequestAns &&
         localDetails.dealStructureAns &&
         localDetails.extraDealDeskDetailsAns
      setIsValid(!!valid)
   }, [localDetails])

   const handleSave = () => {
      setShowValidation(true) // Show validation when Save is pressed

      if (isValid) {
         props.setDetails(localDetails)
         props.onComplete()
         props.onHide()
         setShowValidation(false) // Reset for next time
      }
      // If not valid, keep modal open and show validation errors
   }

   const handleFieldChange = (field: keyof DealDeskDetails, value: string) => {
      setLocalDetails((prev) => ({ ...prev, [field]: value }))
   }

   // Helper function to prevent Enter key crashes while allowing natural textarea behavior
   const handleKeyDown = (
      e: React.KeyboardEvent,
      isTextarea: boolean = false,
   ) => {
      if (e.key === 'Enter') {
         if (isTextarea) {
            // Allow Enter key in textareas for natural line breaks
            return
         }
         // Prevent Enter key only in single-line text inputs to avoid form submission crashes
         e.preventDefault()
      }
   }

   return (
      <Modal show={props.show} onHide={props.onHide} centered size='lg'>
         <Modal.Header closeButton>
            <Modal.Title>{props.title}</Modal.Title>
         </Modal.Header>
         <Modal.Body>
            <Form
               onSubmit={(e) => {
                  e.preventDefault() // Prevent default form submission
                  handleSave() // Call our save handler instead
               }}
            >
               <div className='mb-3'>
                  <Form.Label>
                     {props.getString(
                        'product.DealdeskModalButton.Modal.DealDeskDetailsModal.Form.CallRecordingLink.Label',
                     )}
                     <span className='text-danger'> *</span>
                  </Form.Label>
                  <Form.Control
                     type='text'
                     value={localDetails.callRecordingLink}
                     onChange={(e) =>
                        handleFieldChange('callRecordingLink', e.target.value)
                     }
                     onKeyDown={(e) => handleKeyDown(e)}
                     isInvalid={
                        showValidation && !localDetails.callRecordingLink
                     }
                     placeholder={props.getString(
                        'product.DealdeskModalButton.Modal.DealDeskDetailsModal.Form.CallRecordingLink.Placeholder',
                     )}
                  />
                  {showValidation && !localDetails.callRecordingLink && (
                     <Form.Control.Feedback type='invalid'>
                        {props.getString('global.RequiredFieldWarning')}
                     </Form.Control.Feedback>
                  )}
               </div>

               {/* Relationship Context */}
               <div className='mb-3'>
                  <Description
                     className=''
                     placement='bottom'
                     content={
                        <div
                           dangerouslySetInnerHTML={{
                              __html: props.getString(
                                 'product.DealdeskModalButton.Modal.DealDeskDetailsModal.Form.relationshipContext.HoverText',
                              ),
                           }}
                        />
                     }
                  >
                     <Form.Label>
                        {props.getString(
                           'product.DealdeskModalButton.Modal.DealDeskDetailsModal.Form.relationshipContext.Label',
                        )}
                        <span className='text-danger'> *</span>
                     </Form.Label>
                  </Description>
                  <Form.Select
                     value={localDetails.relationshipHistory}
                     onChange={(e) =>
                        handleFieldChange('relationshipHistory', e.target.value)
                     }
                     isInvalid={
                        showValidation && !localDetails.relationshipHistory
                     }
                     className={`form-select-dd ${localDetails.relationshipHistory === '' ? 'placeholder-selected' : ''}`}
                  >
                     <option value='' disabled>
                        Choose...
                     </option>
                     <option value='New prospect'>New Prospect</option>
                     <option value='Existing Customer'>
                        Existing Customer
                     </option>
                     <option value='Former Customer'>Former Customer</option>
                  </Form.Select>

                  {showValidation && !localDetails.relationshipHistory && (
                     <Form.Control.Feedback type='invalid'>
                        {props.getString('global.RequiredFieldWarning')}
                     </Form.Control.Feedback>
                  )}
               </div>

               <div className='mb-3'>
                  <Form.Control
                     as='textarea'
                     rows={4}
                     value={localDetails.relationshipContextAns}
                     onChange={(e) =>
                        handleFieldChange(
                           'relationshipContextAns',
                           e.target.value,
                        )
                     }
                     onKeyDown={(e) => handleKeyDown(e, true)}
                     isInvalid={
                        showValidation && !localDetails.relationshipContextAns
                     }
                  />
                  {showValidation && !localDetails.relationshipContextAns && (
                     <Form.Control.Feedback type='invalid'>
                        {props.getString('global.RequiredFieldWarning')}
                     </Form.Control.Feedback>
                  )}
               </div>

               <div className='mb-3'>
                  <Description
                     className=''
                     placement='bottom'
                     content={
                        <div
                           dangerouslySetInnerHTML={{
                              __html: props.getString(
                                 'product.DealdeskModalButton.Modal.DealDeskDetailsModal.Form.nonStandardRequestAns.HoverText',
                              ),
                           }}
                        />
                     }
                  >
                     <Form.Label>
                        {props.getString(
                           'product.DealdeskModalButton.Modal.DealDeskDetailsModal.Form.nonStandardRequestAns.Label',
                        )}
                        <span className='text-danger'> *</span>
                     </Form.Label>
                  </Description>

                  <Form.Select
                     value={localDetails.hasCombinedOffers}
                     onChange={(e) =>
                        handleFieldChange('hasCombinedOffers', e.target.value)
                     }
                     isInvalid={
                        showValidation && !localDetails.hasCombinedOffers
                     }
                     className={`form-select-dd ${localDetails.hasCombinedOffers === '' ? 'placeholder-selected' : ''}`}
                  >
                     <option value='' disabled>
                        {props.getString(
                           'product.DealdeskModalButton.Modal.DealDeskDetailsModal.Form.combinedOffersCodes.Placeholder',
                        )}
                     </option>
                     <option value='No'>No</option>
                     <option value='Yes'>Yes</option>
                  </Form.Select>
                  {showValidation && !localDetails.hasCombinedOffers && (
                     <Form.Control.Feedback type='invalid'>
                        {props.getString('global.RequiredFieldWarning')}
                     </Form.Control.Feedback>
                  )}
               </div>

               <div className='mb-3'>
                  <Form.Control
                     as='textarea'
                     rows={4}
                     value={localDetails.nonStandardRequestAns}
                     onChange={(e) =>
                        handleFieldChange(
                           'nonStandardRequestAns',
                           e.target.value,
                        )
                     }
                     onKeyDown={(e) => handleKeyDown(e, true)}
                     isInvalid={
                        showValidation && !localDetails.nonStandardRequestAns
                     }
                  />
                  {showValidation && !localDetails.nonStandardRequestAns && (
                     <Form.Control.Feedback type='invalid'>
                        {props.getString('global.RequiredFieldWarning')}
                     </Form.Control.Feedback>
                  )}
               </div>

               <div className='mb-3'>
                  <Description
                     className=''
                     placement='bottom'
                     content={
                        <div
                           dangerouslySetInnerHTML={{
                              __html: props.getString(
                                 'product.DealdeskModalButton.Modal.DealDeskDetailsModal.Form.dealStructureAns.HoverText',
                              ),
                           }}
                        />
                     }
                  >
                     <Form.Label>
                        {props.getString(
                           'product.DealdeskModalButton.Modal.DealDeskDetailsModal.Form.dealStructureAns.Label',
                        )}
                        <span className='text-danger'> *</span>
                     </Form.Label>
                  </Description>

                  <Form.Control
                     as='textarea'
                     rows={4}
                     value={localDetails.dealStructureAns}
                     onChange={(e) =>
                        handleFieldChange('dealStructureAns', e.target.value)
                     }
                     onKeyDown={(e) => handleKeyDown(e, true)}
                     isInvalid={
                        showValidation && !localDetails.dealStructureAns
                     }
                  />
                  {showValidation && !localDetails.dealStructureAns && (
                     <Form.Control.Feedback type='invalid'>
                        {props.getString('global.RequiredFieldWarning')}
                     </Form.Control.Feedback>
                  )}
               </div>

               <div className='mb-3'>
                  <Description
                     className=''
                     placement='bottom'
                     content={
                        <div
                           dangerouslySetInnerHTML={{
                              __html: props.getString(
                                 'product.DealdeskModalButton.Modal.DealDeskDetailsModal.Form.extraDealDeskDetailsAns.HoverText',
                              ),
                           }}
                        />
                     }
                  >
                     <Form.Label>
                        {props.getString(
                           'product.DealdeskModalButton.Modal.DealDeskDetailsModal.Form.extraDealDeskDetailsAns.Label',
                        )}
                        <span className='text-danger'> *</span>
                     </Form.Label>
                  </Description>
                  <Form.Control
                     as='textarea'
                     rows={4}
                     value={localDetails.extraDealDeskDetailsAns}
                     onChange={(e) =>
                        handleFieldChange(
                           'extraDealDeskDetailsAns',
                           e.target.value,
                        )
                     }
                     onKeyDown={(e) => handleKeyDown(e, true)}
                     isInvalid={
                        showValidation && !localDetails.extraDealDeskDetailsAns
                     }
                  />
                  {showValidation && !localDetails.extraDealDeskDetailsAns && (
                     <Form.Control.Feedback type='invalid'>
                        {props.getString('global.RequiredFieldWarning')}
                     </Form.Control.Feedback>
                  )}
               </div>
            </Form>
         </Modal.Body>
         <Modal.Footer>
            <Button variant='secondary' onClick={props.onHide}>
               {props.getString('global.Cancel')}
            </Button>
            <Button variant='primary' onClick={handleSave} disabled={!isValid}>
               {props.getString('global.Save')}
            </Button>
         </Modal.Footer>
      </Modal>
   )
}

// Define the DealDeskTypeProps interface
interface DealDeskTypeProps {
   order: number
   label: string
   disabled: boolean
   checked: boolean
   setChecked: (value: boolean) => void
   disablingDiscounts?: DisablingDiscount[]
   getFormattedString:
      | ((
           key: string,
           variables?: Record<string, string | number>,
           fallback?: string,
        ) => string)
      | undefined
}
//TODO: use Form.Check instead of a generic checkbox, add validation to enforce at least one checkbox is selected
function DealDeskType(props: DealDeskTypeProps) {
   // Create a safe version of getFormattedString that handles undefined
   const safeGetFormattedString = (
      key: string,
      variables?: Record<string, string | number>,
      fallback?: string,
   ): string => {
      return props.getFormattedString
         ? props.getFormattedString(key, variables, fallback)
         : key
   }

   return (
      <div className='row' style={{ order: props.order }}>
         <div className='col-bhr1'>{props.label}</div>
         <div className='col'>
            {props.disabled && (props.disablingDiscounts?.length ?? 0 > 0) ? (
               <Description
                  title='Disabling Discounts'
                  className={'width-text'}
                  content={
                     <>
                        <div
                           dangerouslySetInnerHTML={{
                              __html: safeGetFormattedString(
                                 'product.DealdeskModalButton.Modal.DealDeskType.Description',
                                 { label: props.label },
                              ),
                           }}
                        />
                        <DisablingDiscountsList
                           disablingDiscounts={props.disablingDiscounts || []}
                        />
                     </>
                  }
               >
                  <input
                     className='input-field'
                     type='checkbox'
                     checked={props.checked}
                     disabled={props.disabled}
                     onChange={() => props.setChecked(!props.checked)}
                  />
               </Description>
            ) : (
               <input
                  className='input-field'
                  type='checkbox'
                  checked={props.checked}
                  disabled={props.disabled}
                  onChange={() => props.setChecked(!props.checked)}
               />
            )}
         </div>
      </div>
   )
}

interface DealDeskPercentInputProps {
   order: number
   label: string
   listPriceLabel: string
   discountLabel: string
   listPrice: number
   discountPriceLabel: string
   discountPercent: number
   discountPercentValid: boolean
   discountPercentInvalid: boolean
   setValidFunction: (value: boolean) => void
   setInvalidFunction: (value: boolean) => void
   setDiscountPercent: (value: number) => void
   comments: string
   setComments: (value: string) => void
   commentsValid: boolean
   commentsInvalid: boolean
   setCommentsValidFunction: (value: boolean) => void
   setCommentsInvalidFunction: (value: boolean) => void
   currencyObject: currencyObject
   className: string
   getString: (key: string, fallback?: string) => string
}

// Interface for input components without comments
interface DealDeskPercentInputWithoutCommentsProps {
   order: number
   label: string
   listPriceLabel: string
   discountLabel: string
   listPrice: number
   discountPriceLabel: string
   discountPercent: number
   discountPercentValid: boolean
   discountPercentInvalid: boolean
   setValidFunction: (value: boolean) => void
   setInvalidFunction: (value: boolean) => void
   setDiscountPercent: (value: number) => void
   currencyObject: currencyObject
   className: string
   getString: (key: string, fallback?: string) => string
}

function DealDeskPercentInput(props: DealDeskPercentInputProps) {
   return (
      <div className={props.className}>
         <b>{props.label}</b>
         <div className='row' style={{ order: props.order }}>
            <div className='col-bhr1'>
               <InputGroup size='sm' className='mb-1'>
                  <InputGroup.Text>{props.discountLabel}</InputGroup.Text>
                  <Form.Control
                     style={{ textAlign: 'right' }}
                     aria-label='Requested Discount Percentage'
                     type='number'
                     value={
                        props.discountPercent <= 0
                           ? ''
                           : props.discountPercent.toString()
                     }
                     onChange={(e) =>
                        onPercentInputChange(
                           Number(e.target.value),
                           props.discountPercent,
                           props.setDiscountPercent,
                           props.discountPercentInvalid,
                           props.discountPercentValid,
                           props.setValidFunction,
                           props.setInvalidFunction,
                        )
                     }
                     isValid={props.discountPercentValid}
                     isInvalid={props.discountPercentInvalid}
                     min={0}
                     max={100}
                     placeholder='0'
                  />
                  <InputGroup.Text>%</InputGroup.Text>
                  <Form.Control.Feedback type='invalid'>
                     {props.getString(
                        'product.DealdeskModalButton.Modal.DealDeskPercentInput.Form.Feedback',
                     )}
                  </Form.Control.Feedback>
               </InputGroup>
               <DealDeskPricing
                  listPrice={props.listPrice}
                  discountPercent={props.discountPercent}
                  currencyObject={props.currencyObject}
                  discountPriceLabel={props.discountPriceLabel}
                  listPriceLabel={props.listPriceLabel}
               />
            </div>
            <DealDeskCommentInput
               comments={props.comments}
               commentsValid={props.commentsValid}
               commentsInvalid={props.commentsInvalid}
               setCommentsValidFunction={props.setCommentsValidFunction}
               setCommentsInvalidFunction={props.setCommentsInvalidFunction}
               setComments={props.setComments}
               getString={props.getString}
            />
         </div>
      </div>
   )
}

function DealDeskPercentInputWithoutComments(
   props: DealDeskPercentInputWithoutCommentsProps,
) {
   return (
      <div className={props.className}>
         <b>{props.label}</b>
         <div className='row' style={{ order: props.order }}>
            <div className='col-bhr1'>
               <InputGroup size='sm' className='mb-1'>
                  <InputGroup.Text>{props.discountLabel}</InputGroup.Text>
                  <Form.Control
                     style={{ textAlign: 'right' }}
                     aria-label='Requested Discount Percentage'
                     type='number'
                     value={
                        props.discountPercent <= 0
                           ? ''
                           : props.discountPercent.toString()
                     }
                     onChange={(e) =>
                        onPercentInputChange(
                           Number(e.target.value),
                           props.discountPercent,
                           props.setDiscountPercent,
                           props.discountPercentInvalid,
                           props.discountPercentValid,
                           props.setValidFunction,
                           props.setInvalidFunction,
                        )
                     }
                     isValid={props.discountPercentValid}
                     isInvalid={props.discountPercentInvalid}
                     min={0}
                     max={100}
                     placeholder='0'
                  />
                  <InputGroup.Text>%</InputGroup.Text>
                  <Form.Control.Feedback type='invalid'>
                     {props.getString(
                        'product.DealdeskModalButton.Modal.DealDeskPercentInputWithoutComments.Form.Feedback',
                     )}
                  </Form.Control.Feedback>
               </InputGroup>
               <DealDeskPricing
                  listPrice={props.listPrice}
                  discountPercent={props.discountPercent}
                  currencyObject={props.currencyObject}
                  discountPriceLabel={props.discountPriceLabel}
                  listPriceLabel={props.listPriceLabel}
               />
            </div>
         </div>
      </div>
   )
}

interface DealDeskPercentPicklistInputProps extends DealDeskPercentInputProps {
   picklistOptions: number[]
   getString: (key: string, fallback?: string) => string
}

interface DealDeskPercentPicklistInputWithoutCommentsProps
   extends DealDeskPercentInputWithoutCommentsProps {
   picklistOptions: number[]
   getString: (key: string, fallback?: string) => string
}

function DealDeskPercentPicklistInput(
   props: DealDeskPercentPicklistInputProps,
) {
   return (
      <div className={props.className}>
         <b>{props.label}</b>
         <div className='row' style={{ order: props.order }}>
            <div className='col-bhr1'>
               <InputGroup size='sm' className='mb-1'>
                  <InputGroup.Text>{props.discountLabel}</InputGroup.Text>
                  <Dropdown>
                     <Dropdown.Toggle
                        variant='outline-secondary'
                        className={`form-control ${props.discountPercentValid ? 'is-valid' : ''} ${props.discountPercentInvalid ? 'is-invalid' : ''}`}
                        style={{ textAlign: 'right' }}
                     >
                        {props.discountPercent}
                     </Dropdown.Toggle>
                     <Dropdown.Menu>
                        {props.picklistOptions.map((option) => (
                           <Dropdown.Item
                              key={option}
                              onClick={() =>
                                 onPercentInputChange(
                                    option,
                                    props.discountPercent,
                                    props.setDiscountPercent,
                                    props.discountPercentInvalid,
                                    props.discountPercentValid,
                                    props.setValidFunction,
                                    props.setInvalidFunction,
                                 )
                              }
                           >
                              {option}
                           </Dropdown.Item>
                        ))}
                     </Dropdown.Menu>
                  </Dropdown>
                  <InputGroup.Text>%</InputGroup.Text>
                  <Form.Control.Feedback type='invalid'>
                     {props.getString(
                        'product.DealdeskModalButton.Modal.DealDeskPercentPicklistInput.Form.Feedback',
                     )}
                  </Form.Control.Feedback>
               </InputGroup>
               <DealDeskPricing
                  listPrice={props.listPrice}
                  discountPercent={props.discountPercent}
                  currencyObject={props.currencyObject}
                  discountPriceLabel={props.discountPriceLabel}
                  listPriceLabel={props.listPriceLabel}
               />
            </div>
            <DealDeskCommentInput
               comments={props.comments}
               commentsValid={props.commentsValid}
               commentsInvalid={props.commentsInvalid}
               setCommentsValidFunction={props.setCommentsValidFunction}
               setCommentsInvalidFunction={props.setCommentsInvalidFunction}
               setComments={props.setComments}
               getString={props.getString}
            />
         </div>
      </div>
   )
}

function DealDeskPercentPicklistInputWithoutComments(
   props: DealDeskPercentPicklistInputWithoutCommentsProps,
) {
   return (
      <div className={props.className}>
         <b>{props.label}</b>
         <div className='row' style={{ order: props.order }}>
            <div className='col-bhr1'>
               <InputGroup size='sm' className='mb-1'>
                  <InputGroup.Text>{props.discountLabel}</InputGroup.Text>
                  <Dropdown>
                     <Dropdown.Toggle
                        variant='outline-secondary'
                        id='dropdown-basic'
                        style={{ textAlign: 'right' }}
                     >
                        {props.discountPercent <= 0
                           ? '0'
                           : props.discountPercent.toString()}
                        %
                     </Dropdown.Toggle>
                     <Dropdown.Menu>
                        {props.picklistOptions.map((option) => (
                           <Dropdown.Item
                              key={option}
                              onClick={() =>
                                 onPercentInputChange(
                                    option,
                                    props.discountPercent,
                                    props.setDiscountPercent,
                                    props.discountPercentInvalid,
                                    props.discountPercentValid,
                                    props.setValidFunction,
                                    props.setInvalidFunction,
                                 )
                              }
                           >
                              {option}%
                           </Dropdown.Item>
                        ))}
                     </Dropdown.Menu>
                  </Dropdown>
                  <Form.Control.Feedback type='invalid'>
                     {props.getString(
                        'product.DealdeskModalButton.Modal.DealDeskPercentPicklistInputWithoutComments.Form.Feedback',
                     )}
                  </Form.Control.Feedback>
               </InputGroup>
               <DealDeskPricing
                  listPrice={props.listPrice}
                  discountPercent={props.discountPercent}
                  currencyObject={props.currencyObject}
                  discountPriceLabel={props.discountPriceLabel}
                  listPriceLabel={props.listPriceLabel}
               />
            </div>
         </div>
      </div>
   )
}

interface DealDeskPricingProps {
   listPrice: number
   discountPercent: number
   currencyObject: currencyObject
   discountPriceLabel: string
   listPriceLabel: string
   customDiscountedPrice?: number
}

function DealDeskPricing(props: DealDeskPricingProps) {
   // Use custom discounted price if provided, otherwise use standard calculation
   const discountedPrice =
      props.customDiscountedPrice !== undefined
         ? props.customDiscountedPrice
         : props.listPrice - props.listPrice * props.discountPercent * 0.01

   return (
      <>
         <InputGroup size='sm' className='mb-1'>
            <InputGroup.Text>{props.listPriceLabel}</InputGroup.Text>
            <Form.Control
               style={{ textAlign: 'right' }}
               aria-label='List Price'
               type='text'
               disabled={true}
               value={formatFloatAsCurrencySymbol(
                  props.listPrice,
                  props.currencyObject,
               )}
            />
         </InputGroup>
         <InputGroup size='sm'>
            <InputGroup.Text>{props.discountPriceLabel}</InputGroup.Text>
            <Form.Control
               className='dealdesk-price-with-discount'
               style={{ textAlign: 'right' }}
               aria-label='Discounted Price'
               type='text'
               disabled={true}
               value={formatFloatAsCurrencySymbol(
                  discountedPrice,
                  props.currencyObject,
                  true,
               )}
            />
         </InputGroup>
      </>
   )
}

interface DealDeskCommentInputProps {
   comments: string
   commentsValid: boolean
   commentsInvalid: boolean
   setCommentsValidFunction: (value: boolean) => void
   setCommentsInvalidFunction: (value: boolean) => void
   setComments: (value: string) => void
   getString: (key: string, fallback?: string) => string
}

function DealDeskCommentInput(props: DealDeskCommentInputProps) {
   return (
      <div className='col mb-2'>
         <InputGroup size='lg'>
            <InputGroup.Text>
               <div
                  dangerouslySetInnerHTML={{
                     __html: props.getString(
                        'product.DealdeskModalButton.Modal.DealDeskCommentInput.Text',
                     ),
                  }}
               />
            </InputGroup.Text>
            <Form.Control
               required
               className='dealdesk-comments'
               style={{ fontSize: '10pt', height: '6.25em' }}
               aria-label='Additional information for discount request'
               as='textarea'
               placeholder={props.getString(
                  'product.DealdeskModalButton.Modal.DealDeskCommentInput.Placeholder',
               )}
               onChange={(e) => props.setComments(e.target.value)}
               value={props.comments}
               isInvalid={props.commentsInvalid}
               maxLength={32768}
            />
            <Form.Control.Feedback type='invalid'>
               {props.getString(
                  'product.DealdeskModalButton.Modal.DealDeskCommentInput.Feedback',
               )}
            </Form.Control.Feedback>
         </InputGroup>
      </div>
   )
}

type DisablingDiscount = {
   Id: number
   BookId: number
   DiscId: number
   ApplyTo: string
   Priority: number
   CreatedAt: string
   UpdatedAt: string
   BillingFreq: string
   DiscountCode: string
   DiscountName: string
   DiscountType: string
   RoleRequired: string
   ProductFamily: string
   DiscountAmount: number
   actualEffect: DiscountActualEffect
   MRRTotal: number
}
interface DisablingDiscountsProps {
   disablingDiscounts: DisablingDiscount[]
}

function DisablingDiscountsList(props: DisablingDiscountsProps) {
   return (
      <div>
         {props.disablingDiscounts?.map((discount) => (
            <div key={discount.DiscountCode} style={{ textAlign: 'left' }}>
               <p>{discount.DiscountName}</p>
            </div>
         ))}
      </div>
   )
}

/**
 * Submits the deal desk details by constructing the request payload and making an API call.
 *
 * Depending on which deal desk types are selected, this function constructs corresponding deal desk submission objects,
 * aggregates them into the payload, and sends a POST request to the '/api/submit-dealdesk' endpoint.
 *
 * @param {recordType} recordType - The record type (e.g., New, Expansion) for the submission.
 * @param {string} opportunity_id - The unique identifier for the opportunity.
 * @param {boolean} HRISImplementation - Flag indicating if HRIS Implementation is selected.
 * @param {number} HRISImplementationDiscountPercent - The discount percentage for HRIS Implementation.
 * @param {string} HRISImplementationComments - The comments for HRIS Implementation.
 * @param {boolean} QuickStart - Flag indicating if QuickStart is selected.
 * @param {number} QuickStartDiscountPercent - The discount percentage for QuickStart.
 * @param {string} QuickStartComments - The comments for QuickStart.
 * @param {boolean} QuickStartQualifier - Additional qualifier for QuickStart submissions.
 * @param {boolean} PayrollImplementation - Flag indicating if Payroll Implementation is selected.
 * @param {number} PayrollImplementationDiscountPercent - The discount percentage for Payroll Implementation.
 * @param {string} PayrollImplementationComments - The comments for Payroll Implementation.
 * @param {boolean} MRRDiscount - Flag indicating if MRR Discount is selected.
 * @param {DealDeskDetails} MRRDiscountDetails - The details for MRR Discount.
 * @param {boolean} isBenAdminOnly - Flag indicating if this is a Ben Admin only scenario.
 * @returns {Promise<Response>} A promise that resolves to the API response from submitting the deal desk.
 */
async function SubmitDealDesk(
   recordType: recordType,
   opportunity_id: string,
   HRISImplementation: boolean,
   HRISImplementationDiscountPercent: number,
   HRISImplementationDetails: DealDeskDetails,
   QuickStart: boolean,
   QuickStartDiscountPercent: number,
   QuickStartDetails: DealDeskDetails,
   QuickStartQualifier: boolean,
   PayrollImplementation: boolean,
   PayrollImplementationDiscountPercent: number,
   PayrollImplementationDetails: DealDeskDetails,
   MRRDiscount: boolean,
   MRRDiscountDetails: DealDeskDetails,
   isBenAdminOnly: boolean,
   termLength: number,
   prepayLength: number,
   currentMRR: number,
) {
   const host_url = process.env.REACT_APP_HOST_URL

   // For Ben Admin only scenarios, use "Expansion HRIS Implementation" instead of standard logic
   const hrisSubmissionType = isBenAdminOnly
      ? 'Expansion HRIS Implementation'
      : 'New Sale HRIS Implementation'

   const HRISDealDeskSubmission = {
      type: hrisSubmissionType,
      discountPercent: HRISImplementationDiscountPercent,
      comments: formatForSalesforceRichText(
         HRISImplementationDetails.additionalInformation,
      ),
      callRecordingLink: HRISImplementationDetails.callRecordingLink,
      relationshipHistory: HRISImplementationDetails.relationshipHistory,
      relationshipContextAns: HRISImplementationDetails.relationshipContextAns,
      nonStandardRequestAns: HRISImplementationDetails.nonStandardRequestAns,
      hasCombinedOffers: HRISImplementationDetails.hasCombinedOffers,
      dealStructureAns: HRISImplementationDetails.dealStructureAns,
      extraDealDeskDetailsAns:
         HRISImplementationDetails.extraDealDeskDetailsAns,
      termLength: termLength,
      upfrontPaymentLength: prepayLength,
      billingFreq: 'onetime',
   }
   const QuickStartDealDeskSubmission = {
      type: 'Quickstart',
      discountPercent: QuickStartDiscountPercent,
      comments: formatForSalesforceRichText(
         QuickStartDetails.additionalInformation,
      ),
      qualifier: QuickStartQualifier,
      termLength: termLength,
      upfrontPaymentLength: prepayLength,
      billingFreq: 'onetime',
   }
   const PayrollImpDealDeskSubmission = {
      type: 'Payroll Sale Implementation',
      discountPercent: PayrollImplementationDiscountPercent,
      comments: formatForSalesforceRichText(
         PayrollImplementationDetails.additionalInformation,
      ),
      callRecordingLink: PayrollImplementationDetails.callRecordingLink,
      relationshipHistory: PayrollImplementationDetails.relationshipHistory,
      relationshipContextAns:
         PayrollImplementationDetails.relationshipContextAns,
      nonStandardRequestAns: PayrollImplementationDetails.nonStandardRequestAns,
      hasCombinedOffers: PayrollImplementationDetails.hasCombinedOffers,
      dealStructureAns: PayrollImplementationDetails.dealStructureAns,
      extraDealDeskDetailsAns:
         PayrollImplementationDetails.extraDealDeskDetailsAns,
      termLength: termLength,
      upfrontPaymentLength: prepayLength,
      billingFreq: 'onetime',
   }
   const MRRDiscountDealDeskSubmission = {
      type: 'MRR Discount Request',
      discountPercent: 0,
      comments: formatForSalesforceRichText(
         MRRDiscountDetails.additionalInformation,
      ),
      callRecordingLink: MRRDiscountDetails.callRecordingLink,
      relationshipHistory: MRRDiscountDetails.relationshipHistory,
      relationshipContextAns: MRRDiscountDetails.relationshipContextAns,
      nonStandardRequestAns: MRRDiscountDetails.nonStandardRequestAns,
      hasCombinedOffers: MRRDiscountDetails.hasCombinedOffers,
      dealStructureAns: MRRDiscountDetails.dealStructureAns,
      extraDealDeskDetailsAns: MRRDiscountDetails.extraDealDeskDetailsAns,
      termLength: termLength,
      upfrontPaymentLength: prepayLength,
      billingFreq: 'onetime',
   }
   let dealDeskSubmissions = []
   if (HRISImplementation) {
      dealDeskSubmissions.push(HRISDealDeskSubmission)
   }
   if (QuickStart) {
      dealDeskSubmissions.push(QuickStartDealDeskSubmission)
   }
   if (PayrollImplementation) {
      dealDeskSubmissions.push(PayrollImpDealDeskSubmission)
   }
   if (MRRDiscount) {
      dealDeskSubmissions.push(MRRDiscountDealDeskSubmission)
   }

   const body = {
      opportunity_id: opportunity_id,
      recordType: recordType,
      dealDeskSubmissions: dealDeskSubmissions,
      currentMRR: currentMRR,
   }

   // Make the API call to submit the deal desk
   return fetch(`${host_url}/api/submit-dealdesk`, {
      method: 'POST',
      headers: {
         'Content-Type': 'application/json',
      },
      body: JSON.stringify(body),
   })
}

function translateTypes(types: string) {
   let typeList = types.split(',')
   const typeMapping: { [key: string]: string } = {
      'Expansion Sale HRIS Implementation': 'HRIS Implementation',
      'New Sale HRIS Implementation': 'HRIS Implementation',
      Quickstart: 'QuickStart',
      'Payroll Sale Implementation': 'Payroll Implementation',
   }

   return typeList
      .map((type) => typeMapping[type.trim()] || type.trim())
      .join(', ')
}

/**
 * Retrieves a list of product codes ("ApplyTo") for the given deal desk type from the provided discount list.
 *
 * This function searches the discount_list for a discount where the SFDealDeskType property,
 * which is a comma-separated string, contains the specified DealDeskType. If found, it splits and trims
 * the associated ApplyTo field, returning an array of product codes. If no matching discount is found,
 * an empty array is returned.
 *
 * @param {SFDealDeskType} DealDeskType - The specific deal desk type to look for.
 * @param {{ [key: string]: Discount }} discount_list - An object mapping discount identifiers to Discount objects.
 * @returns {string[]} An array of product codes associated with the matching discount, or an empty array if none match.
 */
function getDealDeskApplyTo(
   DealDeskType: SFDealDeskType,
   discount_list: { [key: string]: Discount },
): string[] {
   //Try to find the dealDesk discount for the provided deal desk type. This finds the discount in the discount_list whose SFDealDeskType contains the provided deal desk type
   const dealDesk = Object.values(discount_list).find((discount) =>
      discount?.SFDealDeskType?.split(',')
         //@ts-ignore
         ?.map((item) => item.trim())
         ?.includes(DealDeskType),
   )
   if (dealDesk) {
      //TODO: Fix Types
      //@ts-ignore
      return dealDesk.ApplyTo?.split(',')?.map((item) => item.trim())
   }
   return []
}

/**
 * Locks a deal desk type by adding a lock for each product code associated with it.
 *
 * This function retrieves the list of product codes ("ApplyTo") for the given deal desk type from the discount list.
 * It then iterates over each product code, trims it, and calls the addDealDeskLock callback to lock it.
 *
 * @param {SFDealDeskType} DealDeskType - The deal desk type to lock.
 * @param {{ [key: string]: Discount }} discount_list - An object mapping discount identifiers to Discount objects.
 * @param {(productCode: string) => void} addDealDeskLock - A callback function that adds a lock for the specified product code.
 */
function lockDealDeskType(
   DealDeskType: SFDealDeskType,
   discount_list: { [key: string]: Discount },
   addDealDeskLock: (productCode: string) => void,
) {
   const applyTo = getDealDeskApplyTo(DealDeskType, discount_list)
   applyTo?.forEach((productCode) => {
      addDealDeskLock(productCode.trim())
   })
}

/**
 * Unlocks a deal desk type by removing the lock for each product code associated with it.
 *
 * This function retrieves the list of product codes ("ApplyTo") for the given deal desk type from the discount list.
 * It then iterates over each product code, trims it, and calls the removeDealDeskLock callback to unlock it.
 *
 * @param {SFDealDeskType} DealDeskType - The deal desk type to unlock.
 * @param {{ [key: string]: Discount }} discount_list - An object mapping discount identifiers to Discount objects.
 * @param {(productCode: string) => void} removeDealDeskLock - A callback function that removes a lock for the specified product code.
 */
function unlockDealDeskType(
   DealDeskType: SFDealDeskType,
   discount_list: { [key: string]: Discount },
   removeDealDeskLock: (productCode: string) => void,
) {
   const applyTo = getDealDeskApplyTo(DealDeskType, discount_list)
   applyTo?.forEach((productCode) => {
      removeDealDeskLock(productCode.trim())
   })
}
